import React from "react";
import { AdminLayout } from "../shared/layout";
import { useQuery } from "@tanstack/react-query";
import { getSafetyStats } from "@/data";
import { StatCard } from "@/components/admin";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ShieldAlert, Activity, AlertTriangle } from "lucide-react";
import { format } from "date-fns";

const CATEGORY_CLS = {
  SELF_HARM: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100",
  VIOLENCE: "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-100",
  SEXUAL_CONTENT: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-100",
  ABUSE: "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-100",
};

export function AdminSafetyScreen() {
  const { data, isLoading } = useQuery({
    queryKey: ["safety-stats"],
    queryFn: getSafetyStats,
  });
  const d = data?.data;
  const byCategory = d?.byCategory || [];
  const byAction = d?.byAction || [];
  const daily = d?.daily || [];
  const recent = d?.recent || [];
  const maxDaily = Math.max(1, ...daily.map((x) => x.count));

  return (
    <AdminLayout
      title="Safety"
      description="Aggregated, anonymized crisis-screening events. No user identities or full content are shown."
      loading={isLoading}
    >
      <div className="space-y-6">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <StatCard title="Total events" value={d?.total ?? "—"} icon={ShieldAlert} />
          <StatCard title="Last 30 days" value={d?.last30 ?? "—"} icon={Activity} />
          <StatCard
            title="Categories"
            value={byCategory.length}
            icon={AlertTriangle}
            description="distinct flag types"
          />
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">By category</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {byCategory.length === 0 && (
                <p className="text-sm text-muted-foreground">No events recorded.</p>
              )}
              {byCategory.map((c) => (
                <div key={c.category} className="flex items-center justify-between text-sm">
                  <Badge className={CATEGORY_CLS[c.category] || ""} variant="secondary">
                    {c.category}
                  </Badge>
                  <span className="font-semibold tabular-nums">{c.count}</span>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">By action taken</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {byAction.length === 0 && (
                <p className="text-sm text-muted-foreground">No events recorded.</p>
              )}
              {byAction.map((a) => (
                <div key={a.action} className="flex items-center justify-between text-sm">
                  <span>{a.action}</span>
                  <span className="font-semibold tabular-nums">{a.count}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Events — last 14 days</CardTitle>
          </CardHeader>
          <CardContent>
            {daily.length === 0 ? (
              <p className="text-sm text-muted-foreground">No events yet.</p>
            ) : (
              <div className="flex items-end gap-1 h-32">
                {daily.map((x, i) => (
                  <div key={i} className="flex-1 flex flex-col items-center justify-end gap-1">
                    <div
                      className="w-full rounded-t bg-red-400/70"
                      style={{ height: `${(x.count / maxDaily) * 100}%` }}
                      title={`${x.date}: ${x.count}`}
                    />
                    <span className="text-[9px] text-muted-foreground">{x.date.slice(5)}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Recent flags (anonymized)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {recent.length === 0 && (
              <p className="text-sm text-muted-foreground">No recent events.</p>
            )}
            {recent.map((e) => (
              <div key={e.id} className="rounded-lg border p-3">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <Badge className={CATEGORY_CLS[e.category] || ""} variant="secondary">
                    {e.category}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    {e.action} · {format(new Date(e.createdAt), "d MMM HH:mm")}
                  </span>
                </div>
                {e.excerpt && (
                  <p className="text-sm text-muted-foreground italic">"{e.excerpt}"</p>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
